/**
 * Chinese (Simplified) dictionary — canonical source of the key space.
 *
 * `en.ts` re-uses this dictionary's type (`Record<keyof typeof zhCN,
 * string>`) so every key here must exist there. Conventions mirror
 * console-web for cross-surface consistency:
 *
 * - Keys are lowercase dot paths: `section.subsection.detail`.
 * - Templates use `{name}` placeholders; `format.ts` substitutes on
 *   render. Unknown placeholders render literally so missing params
 *   fail loudly in QA rather than silently eating text.
 * - Punctuation: Chinese sentences end in `。`, lists use `、`.
 *
 * Scope tracks the 12 sections defined in DESIGN.md §6. When adding a
 * new section copy, add the key here first, then add the English
 * translation in `en.ts`, then use it in the component via `useT()`.
 */
export const zhCN = {
  // ── <head> metadata ───────────────────────────────────────────
  // Synced to `document.title` and `<meta name="description">` at
  // runtime by `I18nProvider` — the static values in `index.html`
  // cover the first paint for the default locale (zh-CN) only.
  'meta.title': '哪都通 — 海外 AI 模型网关 | Claude · GPT 国内直连',
  'meta.description':
    '一个 API Key 接入 GPT · Claude · Gemini 最新模型。国内骨干网直连，按 Token 实时扣费，完全兼容 Anthropic / OpenAI / Google 官方 SDK。',

  // ── Header ─────────────────────────────────────────────────────
  'header.nav.models': '模型',
  'header.nav.pricing': '定价',
  'header.nav.status': '状态',
  'header.nav.tools': '工作流',
  'header.nav.docs': '文档',
  'header.cta.sign_in': '登录',
  'header.cta.get_started': '免费开始',

  // ── Hero ───────────────────────────────────────────────────────
  'hero.title.line1': '海外顶级模型',
  'hero.title.accent': '一键接入',
  'hero.title.line3': '为中国开发者',
  'hero.subtitle':
    '一个 API Key 接入三家最新模型。国内骨干网直连，毫秒级响应；透明人民币计价，按 Token 实时扣费；完全兼容 Anthropic / OpenAI / Google 官方 SDK。',
  'hero.cta.primary': '免费注册',
  'hero.cta.secondary': '查看文档',
  'hero.trust.no_proxy': '无需代理环境',
  'hero.trust.no_expiry': '余额永不过期',
  'hero.trust.pay_as_you_go': '按 Token 实时计费',

  // ── Hero · Terminal ───────────────────────────────────────────
  'hero.terminal.filename': '~/my-app — curl',
  'hero.terminal.p50': 'p50 240ms',
  'hero.terminal.comment': '# 把官方 Anthropic SDK 的 base URL 换一下就行',
  'hero.terminal.streaming_label': '→ streaming',
  'hero.terminal.response':
    '您好，很乐意帮忙。以下是一个基于 token bucket 的 async rate limiter',
  'hero.terminal.meta.model': '模型',
  'hero.terminal.meta.tokens': 'tokens: {in} in / {out} out',
  'hero.terminal.meta.cost': '¥ {cost}',
  'hero.terminal.caption.base_url': '切 Base URL 即可 · SDK 不动',

  // ── Provider row ──────────────────────────────────────────────
  'providers.openai': 'OpenAI',
  'providers.anthropic': 'Anthropic',
  'providers.gemini': 'Google Gemini',
  'providers.coming_soon': 'Coming soon',

  // ── Features ──────────────────────────────────────────────────
  'features.eyebrow': '为什么选我们',
  'features.title.lead': '稳定 极速 ',
  'features.title.accent': '省心',
  'features.subtitle':
    '不是在做“国内镜像”——我们是在做一条符合中国大陆开发者期望的 AI API 基础设施。',
  'features.backbone.title': '国内骨干网直连',
  'features.backbone.body':
    '自建 BGP 入口，三线 CN2 + 全球 PoP 就近回源。无需代理即可稳定访问 OpenAI / Anthropic / Gemini。',
  'features.backbone.meta': 'p50 < 300ms · p95 < 900ms',
  'features.billing.title': '透明人民币计价',
  'features.billing.body':
    '按 Token 实时扣费，不设套餐、不强制预存。支持微信支付充值。',
  'features.billing.meta': '余额永不过期',
  'features.sdk.title': '三家统一 SDK',
  'features.sdk.body':
    '完全兼容 Anthropic / OpenAI / Google 官方 SDK——只需替换 Base URL，零代码改动，已有项目 30 秒切换。',
  'features.sdk.meta': '兼容 /v1/messages · /v1/chat/completions',

  // ── Tools ─────────────────────────────────────────────────────
  'tools.eyebrow': '开箱即接入',
  'tools.title.lead': '为你的 ',
  'tools.title.accent': '工作流',
  'tools.title.trail': ' 而生',
  'tools.subtitle':
    '主流 AI 编辑器 / Agent / CLI 全系兼容。改一个环境变量就能把流量走到我们网关。',
  'tools.claude_code': 'Claude Code',
  'tools.cursor': 'Cursor',
  'tools.vscode': 'VS Code',
  'tools.opencode': 'OpenCode',
  'tools.jetbrains': 'JetBrains',
  'tools.openclaw': 'OpenClaw',
  'tools.footnote':
    '还支持 Codex · Continue · Aider · Zed · Cline · LibreChat · OpenWebUI',
  'tools.footnote.link': '查看完整列表',

  // ── Models ────────────────────────────────────────────────────
  'models.eyebrow': '模型矩阵',
  'models.title.lead': '当下最强的',
  'models.title.accent': '模型能力',
  'models.tag.flagship': '旗舰',
  'models.tag.balanced': '均衡',
  'models.tag.fast': '快速',
  'models.tag.code': '代码',

  // ── Pricing ───────────────────────────────────────────────────
  'pricing.eyebrow': '透明定价',
  'pricing.title.accent': '按量',
  'pricing.title.trail': '人民币结算',
  'pricing.subtitle':
    '按 Token 实时扣费。以下价格为官方价汇率换算，无溢价、无套餐。',
  'pricing.col.model': '模型',
  'pricing.col.official': '官方价 (CNY)',
  'pricing.col.channel': '渠道',
  'pricing.col.multiplier': '倍率',
  'pricing.col.input': '输入 / 1M tokens',
  'pricing.col.output': '输出 / 1M tokens',
  'pricing.col.list': '官方价 (USD)',
  'pricing.col.discount': '折扣',
  'pricing.row.input': '输入/M tokens',
  'pricing.row.output': '输出/M tokens',
  'pricing.footnote': '✱ 汇率跟随官方公布价 · 实际扣费以请求完成时的计价为准',

  // ── How it works ──────────────────────────────────────────────
  'how.eyebrow': '开始只需三步',
  'how.title.lead': '30 秒跑起第一个 ',
  'how.title.accent': '请求',
  'how.step1.title': '创建账号',
  'how.step1.body': '邮箱注册即可开始。无需邀请码、不绑卡。',
  'how.step2.title': '生成 API Key',
  'how.step2.body': '控制台一键生成，可随时吊销。支持多 Key 分项目统计用量。',
  'how.step3.title': '接入应用',
  'how.step3.body.before': '把 Base URL 改成 ',
  'how.step3.body.after': '，填入 API Key，完成。',

  // ── Stats band ────────────────────────────────────────────────
  'stats.users.label': '入驻用户',
  'stats.users.caption': '覆盖电商、教育、SaaS、内容创作等各行业',
  'stats.requests.label': '近 30 天请求',
  'stats.requests.caption': '平均 p50 延迟 240ms · p95 820ms',
  'stats.uptime.label': '24h 可用率',
  'stats.uptime.caption_link': '查看实时状态 →',

  // ── Status page ────────────────────────────────────────────────
  'status.eyebrow': 'Public status',
  'status.header.title': '服务状态',
  'status.title.lead': '服务',
  'status.title.accent': '状态',
  'status.subtitle':
    '按厂商与渠道查看过去 24 小时的可用性。统计窗口为滚动 24 小时，按 5 分钟统计单元求成功率均值；无请求渠道默认显示为健康。',
  'status.summary.headline.healthy': '所有渠道运行正常',
  'status.summary.headline.busy': '部分渠道繁忙',
  'status.summary.headline.unavailable': '部分渠道不可用',
  'status.summary.updated': '最后更新 {time}',
  'status.summary.updated_recent': '最近更新时间 {time}（{age}）',
  'status.summary.updated_pending': '正在等待首个状态快照。',
  'status.summary.window': '展示最近 {hours} 小时的可用性 · 每 {minutes} 分钟一个状态区块',
  'status.summary.total': '渠道总数',
  'status.summary.total_inline': '共 {count} 个渠道',
  'status.action.refresh': '刷新状态',
  'status.state.healthy': '健康',
  'status.state.busy': '繁忙',
  'status.state.unavailable': '不可用',
  'status.vendor.channel_count': '{count} 个渠道',
  'status.channel.availability': '24h 可用率',
  'status.channel.availability_short': 'Uptime',
  'status.channel.requests': '24h 请求数',
  'status.channel.requests_with_count': '24h 请求数 {count}',
  'status.channel.requests_short': 'Requests',
  'status.channel.window': '{hours}h · {minutes}min bucket',
  'status.channel.history_label': '{channel} 过去 24 小时状态矩阵',
  'status.channel.zero_requests': '0 req · 默认健康',
  'status.channel.supplier_unknown': '未标注供应方',
  'status.matrix.start': '24小时前',
  'status.matrix.now': '现在',
  'status.events.eyebrow': '近期事件',
  'status.events.title': '最近 24 小时',
  'status.events.empty': '过去 24 小时没有状态变更。',
  'status.events.view_all': '查看全部事件',
  'status.events.recovered': '可用性已恢复',
  'status.events.degraded': '可用性下降',
  'status.events.fluctuated': '短暂波动',
  'status.events.transition': '{channel} 从 {from} 变为 {to}',
  'status.events.transition_to': '{channel} 进入 {to}',
  'status.error.title': '状态数据暂时不可用',
  'status.error.body': '公共状态接口暂时没有返回数据，请稍后刷新重试。',

  // ── Closing CTA ───────────────────────────────────────────────
  'cta.eyebrow': '开始使用',
  'cta.title.lead': '让 AI 真正 ',
  'cta.title.accent': '跑起来',
  'cta.subtitle': '改一个 Base URL 就能切过来。',
  'cta.primary': '免费注册',
  'cta.secondary': '查看文档',

  // ── Footer ────────────────────────────────────────────────────
  'footer.nav.aria_label': '页脚',
  'footer.link.about': '关于我们',
  'footer.link.privacy': '隐私协议',
  'footer.link.terms': '服务条款',
  'footer.link.support': '联系支持',

  // ── Locale switcher chrome ────────────────────────────────────
  // Native-script language labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — they are UI chrome, not translation.
  // The `aria-label` on the button group IS translated so screen
  // readers announce it in the active locale rather than always
  // hearing "Language" regardless of UI language.
  'i18n.switcher.aria_label': '语言',
} as const;
